export class WordApiService {
  private static paragraphDelimiter = '\n<__paragraph_break__/>\n';
  private static NATIVE_SEARCH_STRING_LIMIT = 255;
  private context: Word.RequestContext;

  // This method must wrap all of the other methods in this class to ensure that the Word context is available.
  async withContext(runnable: () => Promise<unknown>): Promise<unknown> {

    return await Word.run(async (context: Word.RequestContext) => {
      this.context = context;
      runnable = runnable.bind(this);
      return await runnable();
    });
  }

  async getParagraphs(): Promise<Word.Paragraph[]> {
    let paragraphs: Word.Paragraph[] = [];
    const body = this.context.document.body;
    body.load("paragraphs");
    await this.context.sync();
    paragraphs = body.paragraphs.items;
    return paragraphs;
  }

  async getWordFileAsStructuredJsonWithHeadingTextPairs(): Promise<{ heading: string, paragraph: Word.Paragraph, text: string }[]> {
    const structuredData: { heading: string, paragraph: Word.Paragraph, text: string }[] = [];
    const paragraphs = await this.getParagraphs();

    // Map heading levels to built-in style names
    const headingStyles = [
      "Heading1",
      "Heading2",
      "Heading3",
      "Heading4",
      "Heading5",
      "Heading6",
      "Heading7",
      "Heading8",
      "Heading9"
    ];

    // Track the current heading path
    const headingPath: string[] = [];

    for (const paragraph of paragraphs) {
      const text = paragraph.text; // Replace tabs with spaces
      if (!text) continue;

      // Find heading level if any
      const style = paragraph.styleBuiltIn;
      const headingLevel = headingStyles.indexOf(style);

      if (headingLevel !== -1) {
        // If heading is higher or equal, reset deeper levels
        headingPath.length = headingLevel;
        headingPath[headingLevel] = text.trim().replace(/\t/g, '  ');
      }

      // If not a heading and no heading path, use _DOCUMENT_START_
      let headingLabel: string;
      if (headingPath.length === 0) {
        headingLabel = "_DOCUMENT_START_";
      } else {
        // Concatenate heading path up to the last non-empty
        headingLabel = headingPath.filter(Boolean).join(" > ");
      }

      // Only add text if not a heading, or if it's the first line (could be heading and content)
      if (headingLevel === -1) {
        structuredData.push({ heading: headingLabel, text, paragraph });
      }
    }

    return structuredData;
  }

  async findMatchInDocument(text: string): Promise<Word.Range | undefined> {
    const { platform } = Office.context.diagnostics;
    const body = this.context.document.body;

    let rangeCollection;
    if (platform === Office.PlatformType.OfficeOnline || text.length <= WordApiService.NATIVE_SEARCH_STRING_LIMIT) {
      rangeCollection = body.search(text, { matchCase: true, ignorePunct: true, ignoreSpace: true, matchWildcards: false });
      rangeCollection.load('items');
      await this.context.sync();
    }
    // Attempt the built-in search first
    let matched = rangeCollection?.items?.length > 0;
    let range: Word.Range;

    if (!matched) {
      // If no match, try to find by structured data
      const textPairs = await this.getWordFileAsStructuredJsonWithHeadingTextPairs();
      const ranges = await this.getSelectionByText(textPairs, text) ?? [];
      matched = ranges.length > 0;

      if (matched) {
        // console.log('Matched by manual selection:', text);
        range = ranges[0];
      }
    } else {
      // console.log('Matched by search:', text);
      range = rangeCollection.items[0];
    }

    if (!matched) {
      console.warn('No match found for text:', text);
    }
    return range;
  }

  async navigateToRange(range: Word.Range) {
    try {
      range.select();
      range.load('text');
      await this.context.sync();
    } catch (error) {
      console.error('Error when navigating to range:', error);
    }
  }

  async navigateToText(text: string): Promise<boolean> {
    const range = await this.findMatchInDocument(text);
    if (range) {
      await this.navigateToRange(range);
    } else {
      console.warn('No match found in document for text:', text);
    }

    return Boolean(range);
  }

  groupByHeading(structuredData: { heading: string, text: string }[]) {
    const groupedData: { [key: string]: string[] } = {};
    structuredData.forEach(({ heading, text }) => {
      groupedData[heading] ??= [];
      groupedData[heading].push(text);
    });
    return Object.entries(groupedData).map(([heading, texts]) => ({ heading, text: texts.join(WordApiService.paragraphDelimiter) }));
  }

  private normalizeString = (str: string) => {
    return str
      .replace(/\u00A0/g, ' ')
      .replace(/\r\n|\r/g, '\n')
      .replace(/\t/g, ' ')
      .replace(/ +/g, ' ')
      .replace(/\n+/g, '\n')
      .trim();
  };

  async getSelectionByText(structuredData: { heading: string, paragraph: Word.Paragraph, text: string }[], text: string): Promise<Word.Range[]> {
    // Assume that the provided text will be within the grouped structured data
    const groupedData = this.groupByHeading(structuredData);

    // Find the heading that matches the text
    const heading = groupedData.find(pair => this.normalizeString(pair.text).includes(this.normalizeString(text)))?.heading;

    if (!heading) {
      console.warn('No matching heading found for text:', text);
      return null;
    }

    const searchTexts = text.split(WordApiService.paragraphDelimiter).map(part => part.trim()).filter(Boolean);

    const ranges: Word.Range[] = [];
    for (const searchText of searchTexts) {
      for (const pair of structuredData) {
        if (pair.heading === heading && pair.text.includes(searchText)) {
          const foundRange = pair.paragraph.getRange('Whole');
          ranges.push(foundRange);
        }
      }

    }
    return ranges;
  }
}